import React, { useEffect } from 'react'
import SbEditable from 'storyblok-react'
import StoryblokClient from 'storyblok-js-client'
import { SEO } from '@/components/atoms/SEO/SEO'
import { Header } from '@/components/organisms/Header/Header'
import { Footer } from '@/components/organisms/Footer/Footer'
import { HeaderSearch } from '@/components/organisms/HeaderSearch/HeaderSearch'
import { ArticleHelpSection } from '@/components/organisms/ArticleHelpSection/ArticleHelpSection'
import { SEOProps } from '@/components/atoms/SEO/types'
import useStoryblok from '@/hooks/useStoryblok'
import { getOgLocale } from '@/helpers/getOgLocale'
import { searchAndScrollToText } from '@/helpers/searchAndScrollToText'
import { ArticleType } from './Article.types'

const Storyblok = new StoryblokClient({ accessToken: process.env.GATSBY_STORYBLOK_ACCESS_TOKEN })

export function Article({ pageContext, location, pageContent }: ArticleType) {
  const layoutStaticData = pageContext ? JSON.parse(pageContext.layout?.content || '{}') : pageContent.layout
  const story = pageContext ? useStoryblok(pageContext.blok, location) : pageContent.blok
  const { content } = story
  const currentLang = pageContext?.currentLang || pageContent?.currentLang

  const checkHeaderType = (componentName: string) => componentName === 'partial_header_simple'

  const {
    seo_title,
    seo_description,
    set_noarchive,
    set_nofollow,
    set_noindex,
    canonical,
    og_title,
    og_description,
    og_image,
    og_locale,
    og_type,
    og_url,
    twitter_card,
    twitter_creator,
    show_logo_only,
    schema_markup,
    keywords
  } = content

  const alternates = pageContext?.blok.alternates || pageContent?.blok.alternates
  const bodyContent = content.body[0]?.content || content.body[0]?.release_notes[0]?.release_body
  const richTextContent =
    bodyContent &&
    Storyblok.richTextResolver.render(bodyContent, {
      optimizeImages: true
    })
  const plainText = richTextContent.replace(/<(.|\n)*?>/g, '')
  const defaultDescription = plainText.substring(0, 160)

  const seoData: SEOProps = {
    title: story.name,
    seo_title: seo_title || content.title,
    seo_description: seo_description || defaultDescription,
    set_noarchive,
    set_nofollow,
    set_noindex,
    canonical,
    og_title,
    og_description: og_description || defaultDescription,
    og_image,
    og_locale: og_locale || getOgLocale(currentLang?.code),
    og_type,
    og_url,
    twitter_card,
    twitter_creator,
    alternates,
    schema_markup,
    lang: currentLang?.code,
    page_slug: pageContext?.url,
    branch: pageContext?.branch,
    keywords,
    location
  }

  if (story == null) {
    return (
      <div>
        <h3>Editor page</h3>
      </div>
    )
  }

  useEffect(() => {
    const param = location?.search?.substring(1)
    const searchValue = param?.split('=')?.[1]

    if (searchValue) {
      const name = decodeURI(searchValue)
      const pattern = name?.toLowerCase()

      searchAndScrollToText(pattern, 'richtext-content')
    }
  }, [location])

  useEffect(() => {
    window.addEventListener('DOMContentLoaded', () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('id')
          if (entry.intersectionRatio > 0) {
            document.querySelector(`div a[href="#${id}"]`)?.classList.add('active')
          } else {
            document.querySelector(`div a[href="#${id}"]`)?.classList.remove('active')
          }
        })
      })

      document.querySelectorAll('section[id]').forEach((section) => {
        observer.observe(section)
      })
    })
  })

  const finalContent = content.component === 'template_layout' ? content : layoutStaticData
  const headerData = finalContent.header && finalContent.header[0]
  const footerData = finalContent.footer && finalContent.footer[0]
  const headerComponent =
    headerData.component === 'partial_header_search' ? (
      <HeaderSearch {...headerData} currentLang={currentLang} />
    ) : (
      <Header
        {...headerData}
        currentLang={currentLang}
        languages={pageContext?.languages || pageContent?.languages}
        simple={checkHeaderType(headerData.component)}
        showLogoOnly={show_logo_only}
        alternates={story.alternates}
      />
    )

  return (
    // @ts-ignore
    <SbEditable content={content}>
      <SEO {...seoData} />
      {/* @ts-ignore */}
      <SbEditable content={headerData} key={headerData?._uid}>
        {headerData && headerComponent}
      </SbEditable>
      <ArticleHelpSection content={content} slug={pageContext?.url} />
      {/* @ts-ignore */}
      <SbEditable content={footerData} key={footerData?._uid}>
        {footerData && (
          <Footer
            {...footerData}
            currentLang={currentLang}
            alternates={story.alternates}
            languages={pageContext?.languages || pageContent?.languages}
            className="max-w-90 px-24 laptop:px-90 desktop:px-0"
          />
        )}
      </SbEditable>
    </SbEditable>
  )
}

export default Article
